<script setup lang="ts">
import { Pencil } from 'lucide';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { useI18n } from '@/composables/i18n';
import { useTableSort } from '@/composables/tableSort';
import { formatIsoDate } from '@/utils/date';
// Lives under views/federation until #5098 lands SphTableHead, which replaces it everywhere.
import TableSortHeader from '@/views/federation/TableSortHeader.vue';

interface FurtherEducation {
    id: number
    labelling: string
    start: string | null
    end: string | null
    type: string
    institution: string
    graduation: string
    completed: boolean
    editUrl: string
}

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    rows: FurtherEducation[]
}>();

const { i18n } = useI18n();

const ALL_YEARS = 'all';
const WITHOUT_DATE = 'none';

const search = ref('');
const currentYear = new Date().getFullYear();
// Opens on every year, unlike the Urlaub filter: a course list is often years old, and a
// page that greets you empty because nothing happened this year reads as data loss.
const year = ref<string>(ALL_YEARS);

/**
 * The years an entry belongs to, from the first of its dates to the last.
 *
 * A half-dated entry counts as the single day it does have rather than as an open range:
 * no write path can produce one any more, and stretching it would list a 2016 course under
 * every year since. With neither date there is nothing to anchor it to, so it answers no
 * year at all and reaches the list through "ohne Datum".
 */
function yearsOf(row: FurtherEducation): number[] {
    const from = row.start ?? row.end;
    const to = row.end ?? row.start;

    if (from === null || to === null) {
        return [];
    }

    const years = [];
    for (let candidate = Number(from.slice(0, 4)); candidate <= Number(to.slice(0, 4)); candidate++) {
        years.push(candidate);
    }

    return years;
}

const yearOptions = computed(() => {
    const years = new Set<number>([currentYear]);
    let anyWithoutDate = false;

    props.rows.forEach((row) => {
        const rowYears = yearsOf(row);

        if (rowYears.length === 0) {
            anyWithoutDate = true;
        }

        rowYears.forEach((rowYear) => years.add(rowYear));
    });

    const options = [...years]
        .sort((a, b) => b - a)
        .map((candidate) => ({ value: String(candidate), label: String(candidate) }));

    options.unshift({ value: ALL_YEARS, label: i18n.t('alle') });

    if (anyWithoutDate) {
        options.push({ value: WITHOUT_DATE, label: i18n.t('Ohne Datum') });
    }

    return options;
});

function matchesYear(row: FurtherEducation): boolean {
    if (year.value === ALL_YEARS) {
        return true;
    }

    const years = yearsOf(row);

    if (year.value === WITHOUT_DATE) {
        return years.length === 0;
    }

    return years.includes(Number(year.value));
}

function searchableText(row: FurtherEducation): string {
    return [
        row.labelling,
        formatIsoDate(row.start),
        formatIsoDate(row.end),
        row.type,
        row.institution,
        row.graduation,
    ].join(' ').toLowerCase();
}

const filtered = computed(() => {
    const term = search.value.trim().toLowerCase();

    return props.rows.filter((row) => {
        if (!matchesYear(row)) {
            return false;
        }

        return term === '' || searchableText(row).includes(term);
    });
});

const { sortKey, sortDirection, toggleSort, sorted } = useTableSort(filtered, {
    labelling: (row) => row.labelling,
    // ISO dates sort chronologically as plain strings; an undated entry sorts as an empty
    // string, so it leads the ascending order the way the DataTables view did.
    start: (row) => row.start,
    end: (row) => row.end,
    type: (row) => row.type,
    institution: (row) => row.institution,
    graduation: (row) => row.graduation,
    completed: (row) => (row.completed ? 1 : 0),
});

defineExpose({ filtered, sorted, search, year, yearOptions, sortKey, sortDirection, toggleSort });
</script>

<template>
    <div>
        <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
            <SphField :label="i18n.t('Jahr')">
                <SphSelect
                    v-model="year"
                    :options="yearOptions"
                    :null-option="false"
                />
            </SphField>
            <SphInput
                v-model="search"
                :placeholder="i18n.t('Suche in dieser Liste...')"
            />
        </div>

        <div class="kgr-row">
            <table class="kgr-table kgr-table--block table-groups">
                <thead>
                    <tr class="kgr-table__row">
                        <TableSortHeader
                            :label="i18n.t('Bezeichnung/Kurs')"
                            sort-key="labelling"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Beginn')"
                            sort-key="start"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Ende')"
                            sort-key="end"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Art')"
                            sort-key="type"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Bildungseinrichtung')"
                            sort-key="institution"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Abschluss')"
                            sort-key="graduation"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Abgeschlossen')"
                            sort-key="completed"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in sorted"
                        :key="row.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
                                <div class="kgr-postfix__fluid">
                                    <a
                                        :href="row.editUrl"
                                        class="kgr-link kgr-link--unobtrusive"
                                    >{{ row.labelling }}</a>
                                </div>
                                <div class="kgr-postfix__fixed">
                                    <SphButton
                                        :icon="Pencil"
                                        as="a"
                                        :href="row.editUrl"
                                        :title="i18n.t('Bearbeiten')"
                                        variant="ghost"
                                        size="sm"
                                        class="kgr-table__action"
                                    />
                                </div>
                            </div>
                        </td>
                        <td class="kgr-table__cell">{{ formatIsoDate(row.start) || '-' }}</td>
                        <td class="kgr-table__cell">{{ formatIsoDate(row.end) || '-' }}</td>
                        <td class="kgr-table__cell">{{ row.type }}</td>
                        <td class="kgr-table__cell">{{ row.institution }}</td>
                        <td class="kgr-table__cell">{{ row.graduation }}</td>
                        <td class="kgr-table__cell">
                            <SphCheckbox
                                :model-value="row.completed"
                                disabled
                            />
                        </td>
                    </tr>
                    <tr
                        v-if="sorted.length === 0"
                        class="kgr-table__row"
                    >
                        <td
                            class="kgr-table__cell"
                            colspan="7"
                        >
                            {{ rows.length === 0 ? i18n.t('Keine Fortbildungen vorhanden') : i18n.t('Keine Datensätze vorhanden') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
